import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, InlineError } from "@/components/QueryState";
import { BooMark } from "@/components/boo/BooMark";
import { describeAccessEvent, personName } from "@/components/boo/access-copy";
import { DepthPicker } from "@/components/grants/DepthPicker";
import { DEFAULT_DEPTH, type DepthChoice, type LinkFilter, depthEmpty, depthScopes, filterOf, isGrantScopes, needsApproval } from "@/lib/grant-scopes";
import { ConnectionSheet } from "@/components/grants/ConnectionSheet";
import { OrgOwnerMark, ScopeChips } from "@/components/grants/GrantBits";
import { GrantList } from "@/components/grants/GrantList";
import { HandoverSheet } from "@/components/grants/HandoverSheet";
import { ManageGrantSheet } from "@/components/grants/ManageGrantSheet";
import { ReviewRequestSheet } from "@/components/grants/ReviewRequestSheet";
import { Block, OverrideReason, REASON_MIN, SheetShell } from "@/components/grants/SheetShell";
import { useInvalidateLink } from "@/components/grants/useGrantActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { type AccessLogCategory, type AccessLogEvent, type AccessOverview, type BooLinkRow, type GrantScopes, api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Download, KeyRound, Plus, Search, ShieldAlert, UserCog, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

// The org's Boo Org page: what its Boos share with each other, what changed
// and who decided it, and each Boo's owners, with handing a Boo over or
// training a successor. Org owners and admins see it and can step in for a
// Boo's admins. Copy says "sharing", never "grant", as a Boo's Sharing tab does.
// Its list is the one a Boo's Sharing tab shows, with every Boo's rows.

export type AccessSearch = { tab?: "grants" | "activity" | "handover"; link?: string };
const TABS: Array<{ id: NonNullable<AccessSearch["tab"]>; label: string }> = [
  { id: "grants", label: "Sharing" },
  { id: "activity", label: "Activity" },
  { id: "handover", label: "Owner(s)" },
];

export function AccessPage() {
  useDocumentTitle("Boo Org");
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const search = useSearch({ strict: false }) as AccessSearch;
  const navigate = useNavigate();
  const tab = search.tab ?? "grants";
  const overview = useQuery({ queryKey: ["org-access", orgId], queryFn: () => api.access.overview(), refetchInterval: 15_000 });
  const setTab = (t: AccessSearch["tab"]) => navigate({ to: "/orgs/$orgId/access", params: { orgId }, search: { tab: t } } as never);

  if (overview.isError) return <ErrorComponent error={overview.error} />;
  const data = overview.data;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader title="Boo Org" subtitle="Which Boos learn from each other, what changed, and who owns each Boo." />
      <nav className="flex gap-1 border-b border-border" aria-label="Boo Org sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "grants" && <GrantsTab data={data} initialLink={search.link} />}
      {tab === "activity" && <ActivityTab boos={data?.boos ?? []} />}
      {tab === "handover" && <HandoverTab data={data} />}
    </div>
  );
}

// ── Grants ──────────────────────────────────────────────────────────────────

type Filter = "all" | LinkFilter;
// Status filters, named as the status pills say them; each link falls under one (filterOf), so the counts add up to All.
const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "all", label: "All" },
  { id: "waiting", label: "Needs approval" },
  { id: "active", label: "Active" },
  { id: "removing", label: "Stopping" },
  { id: "declined", label: "Declined" },
];

const inFilter = (l: BooLinkRow, f: Filter) => f === "all" || filterOf(l) === f;

function GrantsTab({ data, initialLink }: { data: AccessOverview | undefined; initialLink?: string }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [booId, setBooId] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(initialLink ?? null);
  const [creating, setCreating] = useState(false);
  const canOverride = data?._permissions.canOverride === true;
  // Chip counts follow the Boo and name filters, so a chip's count is what it shows.
  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.links ?? [])
      .filter((l) => booId === "all" || l.source?.id === booId || l.consumer?.id === booId)
      .filter((l) => !q || `${l.source?.name ?? ""} ${l.consumer?.name ?? ""}`.toLowerCase().includes(q));
  }, [data, booId, query]);
  const rows = useMemo(() => matching.filter((l) => inFilter(l, filter)), [matching, filter]);
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.id, matching.filter((l) => inFilter(l, f.id)).length])) as Record<Filter, number>,
    [matching],
  );
  const open = data?.links.find((l) => l.id === openId);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap rounded-md border border-border p-0.5" role="tablist" aria-label="Filter by status">
          {FILTERS.map((f) =>
            f.id === "declined" && counts.declined === 0 && filter !== "declined" ? null : (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs",
                  filter === f.id ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {f.label}
                {f.id === "waiting" && counts.waiting > 0 ? (
                  <span className="rounded-full bg-foreground px-1.5 text-[11px] font-medium text-background tabular-nums">{counts.waiting}</span>
                ) : (
                  <span className="font-normal text-muted-foreground tabular-nums">{counts[f.id]}</span>
                )}
              </button>
            ),
          )}
        </div>
        <BooFilter boos={data?.boos ?? []} value={booId} onChange={setBooId} />
        <div className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a Boo" aria-label="Find a Boo" className="h-8 pl-8 text-sm" />
        </div>
        {canOverride && (
          <Button size="sm" className="ml-auto" onClick={() => setCreating(true)}>
            <Plus />
            Share between Boos
          </Button>
        )}
      </div>
      {!data ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : (
        <GrantList links={rows} onOpen={(l) => setOpenId(l.id)} empty={data.links.length === 0 ? "Nothing shared between Boos yet" : "No matches"} />
      )}
      {open &&
        (canOverride && needsApproval(open) ? (
          <ReviewRequestSheet key={open.id} actor={{ kind: "org" }} link={open} onClose={() => setOpenId(null)} />
        ) : canOverride && open.status === "approved" ? (
          <ManageGrantSheet key={open.id} actor={{ kind: "org" }} link={open} onClose={() => setOpenId(null)} />
        ) : (
          <ConnectionSheet
            key={open.id}
            containerId={open.consumer?.id ?? ""}
            link={open}
            canManage={false}
            note="Only org owners and admins can change this."
            onClose={() => setOpenId(null)}
          />
        ))}
      {creating && data && <NewGrantSheet boos={data.boos} onClose={() => setCreating(false)} />}
    </section>
  );
}

function BooFilter({ boos, value, onChange }: { boos: AccessOverview["boos"]; value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-48 text-xs" aria-label="Filter by Boo">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Boos</SelectItem>
        {boos.map((b) => (
          <SelectItem key={b.id} value={b.id}>
            {b.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * An org owner or admin sets up what one Boo gets from another, without a
 * request. As `successor`, the same grant framed as training an existing Boo
 * to take over from the source; `onHandOver` switches to handing the source over.
 */
function NewGrantSheet({
  boos,
  onClose,
  initial,
  successor,
  onHandOver,
}: {
  boos: AccessOverview["boos"];
  onClose: () => void;
  initial?: { sourceId?: string; consumerId?: string; depth?: DepthChoice };
  successor?: boolean;
  onHandOver?: (sourceId: string) => void;
}) {
  const [sourceId, setSourceId] = useState(initial?.sourceId ?? "");
  const [consumerId, setConsumerId] = useState(initial?.consumerId ?? "");
  const [depth, setDepth] = useState<DepthChoice>(initial?.depth ?? DEFAULT_DEPTH);
  const [reshare, setReshare] = useState(!successor);
  const [reason, setReason] = useState("");
  const invalidate = useInvalidateLink();
  const source = boos.find((b) => b.id === sourceId);
  const consumer = boos.find((b) => b.id === consumerId);
  const create = useMutation({
    mutationFn: () =>
      api.access.create({
        sourceId,
        consumerId,
        scopes: depth.depth === "custom" ? depthScopes(depth) : { preset: depth.depth },
        reshare,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success(`${consumer?.name} will start learning from ${source?.name} in a moment.`);
      onClose();
    },
    onSettled: () => invalidate({ sourceId, consumerId }),
  });
  const ready = !!source && !!consumer && source.id !== consumer.id && !depthEmpty(depth) && reason.trim().length >= REASON_MIN;
  return (
    <SheetShell
      title={successor ? "Train a successor" : "Share between Boos"}
      initialFocus={successor ? "grant-consumer" : undefined}
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!ready}>
            {successor ? "Start training" : "Set up"}
          </Button>
        </>
      }
    >
      {successor && <SuccessorNote source={source} onHandOver={onHandOver} />}
      <div className="grid gap-3 sm:grid-cols-2">
        <BooSelect id="grant-consumer" label={successor ? "Successor" : "Boo that learns"} boos={boos} value={consumerId} onChange={setConsumerId} exclude={sourceId} />
        <BooSelect id="grant-source" label="Learns from" boos={boos} value={sourceId} onChange={setSourceId} exclude={consumerId} />
      </div>
      <Block title="How much">
        <DepthPicker value={depth} onChange={setDepth} disabled={create.isPending} />
      </Block>
      <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-border px-4 py-3">
        <span>
          <span className="block text-sm font-medium">Allow re-sharing</span>
          <span className="block text-xs text-muted-foreground">Boos that learn from it can get these memories too.</span>
        </span>
        <Switch checked={reshare} onCheckedChange={setReshare} aria-label="Allow re-sharing" />
      </label>
      <OverrideReason value={reason} onChange={setReason} boo={source?.name} />
      {create.isError && <InlineError error={create.error} />}
    </SheetShell>
  );
}

/** What training a successor does, and the way to Hand over instead. */
function SuccessorNote({ source, onHandOver }: { source: AccessOverview["boos"][number] | undefined; onHandOver?: (sourceId: string) => void }) {
  const name = source?.name ?? "this Boo";
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="rounded-md bg-muted/60 px-3 py-2 text-muted-foreground">
        Pick a Boo to learn from {name} while both keep running. It stays up to date until you stop sharing; stopping removes what it learned. Private
        one-on-ones stay out unless you choose Full context.
      </p>
      {source && onHandOver ? (
        <p className="text-muted-foreground">
          Giving {name} itself to someone new?{" "}
          <button type="button" className="font-medium text-foreground underline underline-offset-4 hover:no-underline" onClick={() => onHandOver(source.id)}>
            Hand it over instead.
          </button>
        </p>
      ) : (
        <p className="text-muted-foreground">Giving {name} itself to someone new? Hand it over instead.</p>
      )}
    </div>
  );
}

function BooSelect({ id, label, boos, value, onChange, exclude }: { id: string; label: string; boos: AccessOverview["boos"]; value: string; onChange: (v: string) => void; exclude?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Choose a Boo" />
        </SelectTrigger>
        <SelectContent>
          {boos
            .filter((b) => b.id !== exclude)
            .map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Activity ────────────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<AccessLogCategory | "all", string> = {
  all: "Everything",
  sharing: "Sharing",
  roles: "People and roles",
  handover: "Handovers",
  lifecycle: "Other",
};

function ActivityTab({ boos }: { boos: AccessOverview["boos"] }) {
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const [category, setCategory] = useState<AccessLogCategory | "all">("all");
  const [booId, setBooId] = useState("all");
  const [overrides, setOverrides] = useState(false);
  const q = { category: category === "all" ? undefined : category, booId: booId === "all" ? undefined : booId, overrides };
  const events = useInfiniteQuery({
    queryKey: ["org-access-events", q],
    queryFn: ({ pageParam }) => api.access.events({ ...q, before: pageParam ?? undefined, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
  });
  const exportCsv = useMutation({
    mutationFn: () => api.access.eventsCsv(q),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boo-access-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Couldn't export the log. Try again."),
  });
  const rows = events.data?.pages.flatMap((p) => p.data) ?? [];
  const byDay = groupByDay(rows);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
          <SelectTrigger className="h-8 w-44 text-xs" aria-label="What to show">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(CATEGORY_LABEL) as Array<keyof typeof CATEGORY_LABEL>).map((c) => (
              <SelectItem key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <BooFilter boos={boos} value={booId} onChange={setBooId} />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={overrides} onCheckedChange={setOverrides} aria-label="Only overrides by org admins" />
          Only overrides by org admins
        </label>
        <div className="ml-auto flex items-center gap-3">
          <Link
            to="/orgs/$orgId/audit"
            params={{ orgId }}
            search={{ category: "access" }}
            className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            See the full audit log
          </Link>
          <Button size="sm" variant="outline" onClick={() => exportCsv.mutate()} loading={exportCsv.isPending}>
            <Download />
            Export CSV
          </Button>
        </div>
      </div>
      {events.isLoading ? (
        <Skeleton className="h-60 w-full rounded-lg" />
      ) : events.isError ? (
        <InlineError error={events.error} />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">No activity yet</p>
      ) : (
        <div className="flex flex-col gap-6">
          {byDay.map(([day, items]) => (
            <div key={day} className="flex flex-col gap-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{day}</h3>
              <ol className="flex flex-col divide-y divide-border rounded-lg border border-border">
                {items.map((e) => (
                  <LogRow key={e.id} e={e} />
                ))}
              </ol>
            </div>
          ))}
          {events.hasNextPage && (
            <Button variant="outline" size="sm" className="self-center" onClick={() => events.fetchNextPage()} loading={events.isFetchingNextPage}>
              Load more
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

function groupByDay(rows: AccessLogEvent[]): Array<[string, AccessLogEvent[]]> {
  const out = new Map<string, AccessLogEvent[]>();
  for (const e of rows) {
    const day = new Date(e.createdAt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    out.set(day, [...(out.get(day) ?? []), e]);
  }
  return [...out.entries()];
}

function KindIcon({ kind }: { kind: string }) {
  const cls = "mt-0.5 size-4 shrink-0 text-muted-foreground";
  if (kind === "boo_handed_over" || kind === "memories_archived") return <UserCog className={cls} aria-hidden />;
  if (kind.startsWith("link_")) return <KeyRound className={cls} aria-hidden />;
  return <Users className={cls} aria-hidden />;
}

function LogRow({ e }: { e: AccessLogEvent }) {
  const d = (e.details ?? {}) as Record<string, unknown>;
  const labels = (d.labels ?? {}) as Record<string, string>;
  // roles_changed reuses `removed` for group names.
  const scopesAt = (v: unknown) => (isGrantScopes(v) ? v : null);
  const granted = scopesAt(d.granted ?? d.after);
  const denied = scopesAt(d.denied ?? d.removed);
  return (
    <li className="flex gap-3 px-4 py-3">
      <KindIcon kind={e.kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm [overflow-wrap:anywhere]">
          {describeAccessEvent({ ...e, notice: false }, e.boo.name ?? "a deleted Boo")}
          {/^(role_|ownership_|owner_)/.test(e.kind) && e.boo.name && <span className="text-muted-foreground"> · {e.boo.name}</span>}
        </p>
        {(granted || denied) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {granted && (
              <span className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground">{e.kind === "link_grant_changed" ? "Now:" : "Shared:"}</span>
                <ScopeChips scopes={granted} labels={labels} className="contents" />
              </span>
            )}
            {denied && hasScopes(denied) && (
              <span className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground">{e.kind === "link_grant_changed" ? "Removed:" : "Not shared:"}</span>
                <ScopeChips scopes={denied} labels={labels} className="contents" />
              </span>
            )}
          </div>
        )}
        {e.reason && <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">Reason: “{e.reason}”</p>}
        <p className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground sm:hidden">
          {new Date(e.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          {e.override && <OrgOwnerMark withLabel />}
        </p>
      </div>
      <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
        <span className="text-xs tabular-nums text-muted-foreground">{new Date(e.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
        {e.override && <OrgOwnerMark withLabel />}
      </div>
    </li>
  );
}

function hasScopes(s: GrantScopes) {
  const some = (c: { all: true } | { only: unknown[] }) => "all" in c || c.only.length > 0;
  return some(s.skills) || s.memories.org || some(s.memories.channels) || some(s.memories.people);
}

// ── Handover ────────────────────────────────────────────────────────────────

function HandoverTab({ data }: { data: AccessOverview | undefined }) {
  const [booId, setBooId] = useState<string | null>(null);
  const [successorOf, setSuccessorOf] = useState<string | null>(null);
  const canOverride = data?._permissions.canOverride === true;
  return (
    <section className="flex flex-col gap-4">
      {canOverride && (
        <p className="text-sm text-muted-foreground">
          Hand over gives the same Boo to a new owner. Train successor has another Boo learn from it while both keep running.
        </p>
      )}
      {!data ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {data.boos.map((b) => (
            <li key={b.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
              <span className="flex min-w-0 flex-1 items-start gap-3">
                <BooMark id={b.id} className="mt-0.5 size-6" muted={!b.running} />
                <span className="min-w-0">
                  <span className="block font-medium [overflow-wrap:anywhere]">{b.name}</span>
                  {b.owners.length ? (
                    <span className="block text-xs text-muted-foreground [overflow-wrap:anywhere]">
                      Owned by {b.owners.slice(0, 3).map((o) => personName(o)).join(", ")}
                      {b.owners.length > 3 ? ` and ${b.owners.length - 3} more` : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                      <ShieldAlert className="size-3.5" /> No owner
                    </span>
                  )}
                </span>
              </span>
              {canOverride ? (
                <span className="flex shrink-0 flex-wrap items-center gap-2 pl-9 sm:pl-0">
                  <Button size="sm" variant="outline" onClick={() => setBooId(b.id)}>
                    {b.owners.length ? "Hand over" : "Assign owner"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSuccessorOf(b.id)}>
                    Train successor
                  </Button>
                </span>
              ) : (
                <span className="pl-9 text-xs text-muted-foreground sm:pl-0">Owners only</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {booId && <HandoverSheet booId={booId} onClose={() => setBooId(null)} />}
      {successorOf && data && (
        <NewGrantSheet
          boos={data.boos}
          initial={{ sourceId: successorOf, depth: { ...DEFAULT_DEPTH, depth: "team" } }}
          successor
          onHandOver={(id) => {
            setSuccessorOf(null);
            setBooId(id);
          }}
          onClose={() => setSuccessorOf(null)}
        />
      )}
    </section>
  );
}


